import {
  ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output,
  SimpleChanges, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TimezoneSelectComponent } from '../../../../shared/components/timezone-select/timezone-select.component';
import { MeetingListItem } from '../../models/pcheck.model';
import { browserTimezone, formatZonedDateTimeLine } from '../../helpers/booking-timezone';

/** Payload emitted when the user picks a meeting: the meeting plus the exact
 *  local-time string shown to them (so the chat message reflects what they saw). */
export interface MeetingSelectedEvent {
  meeting: MeetingListItem;
  displayTime: string;
}

@Component({
  selector: 'app-meeting-picker',
  standalone: true,
  imports: [CommonModule, TimezoneSelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './meeting-picker.component.html',
  styleUrls: ['./meeting-picker.component.scss'],
})
export class MeetingPickerComponent implements OnChanges {
  @Input() meetings: MeetingListItem[] = [];
  /** The management flow (tool name) — drives the header verb. */
  @Input() flow = '';
  @Input() disabled = false;
  /** Program-wide zone (Impact: the event timezone). When set, times default
   *  to it instead of the viewer's browser zone. */
  @Input() eventTzIana?: string;
  /** Impact: every time is shown in the event zone — no switching. */
  @Input() lockTimezone = false;

  @Output() meetingSelected = new EventEmitter<MeetingSelectedEvent>();

  readonly displayTz = signal(browserTimezone());

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['eventTzIana'] && this.eventTzIana) this.displayTz.set(this.eventTzIana);
  }

  get heading(): string {
    switch (this.flow) {
      case 'get_reschedule_link':              return 'Select a meeting to reschedule';
      case 'get_cancel_details':               return 'Select a meeting to cancel';
      case 'get_meeting_for_objective_update': return 'Select a meeting to update';
      case 'get_meeting_for_add_participants': return 'Select a meeting to add participants to';
      default:                                 return 'Select a meeting';
    }
  }

  formatTime(isoUtc: string): string {
    return formatZonedDateTimeLine(isoUtc, this.displayTz());
  }

  onTimezoneChange(tz: string): void {
    this.displayTz.set(tz);
  }

  onPick(meeting: MeetingListItem): void {
    if (this.disabled) return;
    this.meetingSelected.emit({ meeting, displayTime: this.formatTime(meeting.start_time) });
  }
}
